import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Filter,
  Building,
  MapPin,
  Mail,
  Phone,
  Edit,
  Trash2,
  Eye
} from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { StatusBadge } from '@components/Card';
import { Dialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { providerService } from '../../services/providerService';
import { Provider, ProviderFilters } from '../../types/provider';

/**
 * Página de listagem de fornecedores conforme especificado no README
 * Inclui filtros, busca e actions baseadas em permissões
 */
export const ProviderListView: React.FC = () => {
  const navigate = useNavigate();
  
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Estatísticas rápidas
  const [totalProviders, setTotalProviders] = useState(0);
  const [activeProviders, setActiveProviders] = useState(0);
  const [inactiveProviders, setInactiveProviders] = useState(0);

  // Carrega os fornecedores
  const loadProviders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const searchFilters: ProviderFilters = {
        page: 1,
        pageSize: 50,
        searchName: search.trim() || undefined
      };
      
      const response = await providerService.getProviders(searchFilters);
      const providersData = Array.isArray(response.data) ? response.data : [];
      setProviders(providersData);
      setTotalProviders(response.pagination?.total || 0);
      
      // Calcula estatísticas - corrigido para usar is_active
      const active = providersData.filter(p => p.is_active !== false).length;
      const inactive = providersData.filter(p => p.is_active === false).length;
      
      setActiveProviders(active);
      setInactiveProviders(inactive);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      loadProviders();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [search]);

  // Deleta fornecedor
  const handleDelete = async (provider: Provider) => {
    try {
      setLoading(true);
      await providerService.deleteProvider(provider.provider_id);
      await loadProviders(); // Recarrega a lista
      setShowDeleteDialog(false);
      setSelectedProvider(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir fornecedor');
    } finally {
      setLoading(false);
    }
  };

  // Renderiza badge de status - corrigido para usar is_active
  const getStatusBadge = (provider: Provider) => {
    return (
      <StatusBadge 
        status={provider.is_active !== false ? 'ativo' : 'inativo'} 
      />
    );
  };

  // Formatar CNPJ/CPF
  const formatDocument = (document: string, type: string) => {
    if (type === 'cnpj' && document.length === 14) {
      return document.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    }
    if (type === 'cpf' && document.length === 11) {
      return document.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    }
    return document;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-h1 mb-2">Fornecedores</h1>
          <p className="text-slate-600">
            Gerenciar cadastro de fornecedores e empresas parceiras
          </p>
        </div>
        <PermissionGuard permission="providers.create">
          <Button
            icon={<Plus size={20} />}
            onClick={() => navigate('/providers/new')}
          >
            Novo Fornecedor
          </Button>
        </PermissionGuard>
      </div>

      {/* Estatísticas rápidas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {totalProviders.toLocaleString('pt-BR')}
                </p>
                <p className="text-small text-slate-500">Total de fornecedores</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Building className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{activeProviders}</p>
                <p className="text-small text-slate-500">Ativos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Building className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{inactiveProviders}</p>
                <p className="text-small text-slate-500">Inativos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50">
          <CardContent>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <MapPin className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {Array.isArray(providers) ? new Set(providers.map(p => p.addr_state)).size : 0}
                </p>
                <p className="text-small text-slate-500">Estados</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e busca */}
      <Card>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome, documento ou cidade..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                startIcon={<Search size={20} />}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" icon={<Filter size={16} />}>
                Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de fornecedores */}
      <Card>
        <CardHeader title="Lista de Fornecedores" />
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-500">Carregando fornecedores...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <Building className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={loadProviders} variant="outline" size="sm">
                Tentar novamente
              </Button>
            </div>
          ) : !Array.isArray(providers) || providers.length === 0 ? (
            <div className="text-center py-8">
              <Building className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">
                {search ? 'Nenhum fornecedor encontrado' : 'Nenhum fornecedor cadastrado'}
              </p>
              <PermissionGuard permission="providers.create">
                <Button onClick={() => navigate('/providers/new')}>
                  Cadastrar primeiro fornecedor
                </Button>
              </PermissionGuard>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <tr>
                  <th className="text-left">Fornecedor</th>
                  <th className="text-left">Documento</th>
                  <th className="text-left">Contato</th>
                  <th className="text-left">Localização</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Ações</th>
                </tr>
              </TableHeader>
              <tbody>
                {Array.isArray(providers) ? providers.map((provider) => (
                  <TableRow key={provider.provider_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">
                          {provider.trade_name || provider.legal_name}
                        </p>
                        {provider.trade_name && provider.trade_name !== provider.legal_name && (
                          <p className="text-sm text-slate-500">
                            {provider.legal_name}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div>
                        <code className="bg-slate-100 px-2 py-1 rounded text-xs">
                          {formatDocument(provider.document, 
                            provider.document.length === 14 ? 'cnpj' : 'cpf'
                          )}
                        </code>
                        {provider.state_registration_tax_id && (
                          <p className="text-xs text-slate-500 mt-1">
                            IE: {provider.state_registration_tax_id}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-1">
                        {provider.email && (
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Mail size={12} />
                            <span className="truncate max-w-32">{provider.email}</span>
                          </div>
                        )}
                        {provider.phone && (
                          <div className="flex items-center gap-1 text-sm text-slate-600">
                            <Phone size={12} />
                            <span>{provider.phone}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <div>
                        <p className="text-sm text-slate-900">{provider.addr_city}</p>
                        <p className="text-xs text-slate-500">{provider.addr_state}</p>
                      </div>
                    </TableCell>
                    
                    <TableCell className="text-center">
                      {getStatusBadge(provider)}
                    </TableCell>
                    
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <PermissionGuard permission="providers.read">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Eye size={16} />}
                            onClick={() => navigate(`/providers/${provider.provider_id}`)}
                            className="px-2"
                          />
                        </PermissionGuard>
                        
                        <PermissionGuard permission="providers.write">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Edit size={16} />}
                            onClick={() => navigate(`/providers/${provider.provider_id}/edit`)}
                            className="px-2"
                          />
                        </PermissionGuard>
                        
                        <PermissionGuard permission="providers.delete">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Trash2 size={16} />}
                            onClick={() => {
                              setSelectedProvider(provider);
                              setShowDeleteDialog(true);
                            }}
                            className="px-2 text-red-600 hover:text-red-700 hover:border-red-300"
                          />
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                )) : null}
              </tbody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title="Confirmar exclusão"
      >
        <div className="space-y-4">
          <p className="text-slate-600">
            Tem certeza que deseja excluir o fornecedor{' '}
            <strong>{selectedProvider?.trade_name || selectedProvider?.legal_name}</strong>?
          </p>
          <p className="text-sm text-red-600">
            Esta ação não pode ser desfeita e removerá todas as informações do fornecedor.
          </p>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedProvider(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={() => selectedProvider && handleDelete(selectedProvider)}
              disabled={loading}
            >
              {loading ? 'Excluindo...' : 'Excluir'}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};